import { datadogRum } from '@datadog/browser-rum';
import { AxiosRequestConfig } from 'axios';

import { formatDateDto } from '@/helpers/formatters';
import DividaDto from '@/models/dto/DividaDto';
import OfertaDto from '@/models/dto/OfertaDto';
import PropostaDto from '@/models/dto/PropostaDto';
import MeioPagamento from '@/models/enums/MeioPagamento';
import { DividaResource, OfertaResource } from '@/models/resources';

import ConvivenciaWlSevice from './ConvivenciaWlSevice';
import HttpService from './HttpService';
import { ApiResponse } from '@/models/resources/ApiResponse';

type OfertaId = OfertaResource['id'];
type DividaId = DividaResource['id'];

interface AceitarOfertaParams {
  api: string,
  ofertaId: OfertaId,
  dividaId: number,
  origem?: string,
  meioPagamentoEscolhido?: MeioPagamento,
}

class OfertaService extends HttpService {
  async aceitarOferta(
    {
      api,
      ofertaId,
      dividaId,
      meioPagamentoEscolhido = MeioPagamento.NaoIdentificado,
      origem = '',
    }: AceitarOfertaParams,
  ): Promise<number | undefined> {
    const reqBody = { ofertaId, meioPagamentoEscolhido };
    const reqConfig: AxiosRequestConfig = origem ? { params: { origem } } : {};
    datadogRum.addAction('sys:oferta_service:aceitar_oferta', {
      api, ofertaId, meioPagamentoEscolhido, origem,
    });

    // Suporte para WhiteLabel que não retorna o id da proposta criada
    if (ConvivenciaWlSevice.isWhiteLabel(api)) {
      await this.post(`/api/${api}/Consumidor/Oferta/${ofertaId}`, reqBody, reqConfig);
      const proposta = await this.obterPropostaGeradaWl(api, dividaId);
      return proposta?.id;
    }

    const response = await this.post<ApiResponse<number>>(
      `/api/${api}/Consumidor/Oferta/${ofertaId}`,
      reqBody,
      reqConfig,
    );
    return response.data;
  }

  async obterDetalhes(
    api: string,
    dividaId: DividaId,
    ofertaId: OfertaId,
  ): Promise<OfertaResource> {
    datadogRum.addAction('sys:oferta_service:obter_detalhes', { api, dividaId, ofertaId });
    const url = `/api/${api}/consumidor/dividas/${dividaId}/ofertas/detalhes`;
    const params = { id: ofertaId };

    return this.get<ApiResponse<OfertaDto>>(url, { params })
      .then((resp) => new OfertaResource(resp.data));
  }

  async obterOfertasPorData(
    api: string,
    dividaId: DividaResource['id'],
    data: Date,
  ): Promise<OfertaResource[]> {
    datadogRum.addAction('sys:oferta_service:obter_ofertas_por_data', { api, dividaId, data });
    const url = `/api/${api}/consumidor/dividas/${dividaId}/ofertas`;
    const params = { data: formatDateDto(data) };

    return this.get<ApiResponse<OfertaDto[]>>(url, { params })
      .then((resp) => resp.data.map((dto) => new OfertaResource(dto)));
  }

  private async obterPropostaGeradaWl(
    api: string,
    dividaId: number,
  ): Promise<PropostaDto | undefined> {
    datadogRum.addAction('sys:divida_service:obter_divida', { api, dividaId });
    const response = await this.get<ApiResponse<DividaDto>>(
      `/api/${api}/Consumidor/Dividas/${dividaId}`,
    );

    if (response.data.ultimaProposta) {
      return response.data.ultimaProposta;
    }
    return undefined;
  }
}

const ofertaService = new OfertaService();
export default ofertaService;
